import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, ArrowRight, Download } from 'lucide-react';
import { productList } from '../data/productData';
import { useTranslation } from 'react-i18next';

const Products = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Package className="w-12 h-12" />
            </div>
            <h1 className="text-5xl font-bold mb-6">{t('products.title')}</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              {t('products.subtitle')}
            </p>
            <div className="mt-8">
              <Link to="/price-list">
                <Button size="lg" className="bg-white text-[#4A5F8F] hover:bg-gray-100">
                  <Download className="mr-2 h-5 w-5" />
                  {t('priceList.contactButton')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {productList.map((product, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300 border-2 border-gray-200 overflow-hidden flex flex-col h-full">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={currentLang === 'en' ? product.name : t(`productData.${product.id}.name`)} 
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>
                <CardContent className="p-8 flex-grow">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {currentLang === 'en' ? product.name : t(`productData.${product.id}.name`)}
                    </h3>
                    <Badge className="bg-[#4A5F8F] hover:bg-[#3A4F7F]">
                      {product.standard}
                    </Badge>
                  </div>
                  
                  <div className="mb-4">
                    <span className="inline-block bg-blue-50 text-[#4A5F8F] px-3 py-1 rounded-full text-sm font-medium">
                      {currentLang === 'en' ? product.range : t(`productData.${product.id}.range`)}
                    </span>
                  </div>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {currentLang === 'en' ? product.description.substring(0, 150) : t(`productData.${product.id}.description`).substring(0, 150)}...
                  </p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {currentLang === 'en' ? 'Key Features:' : t('products.featuresTitle')}
                    </h4>
                    <ul className="grid grid-cols-2 gap-2">
                      {(currentLang === 'en' ? product.features : t(`productData.${product.id}.features`, { returnObjects: true })).slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-3 mt-auto">
                    <Link to={`/products/${product.id}`} className="flex-1">
                      <Button className="w-full bg-[#4A5F8F] hover:bg-[#3A4F7F]">
                        {t('products.viewDetails')} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    {product.catalogUrl && (
                      <a href={product.catalogUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="border-[#4A5F8F] text-[#4A5F8F] hover:bg-[#4A5F8F] hover:text-white">
                          <Download className="h-4 w-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
                {/* Blue accent line at bottom - uniform across all pages */}
                <div className="h-1 bg-[#4A5F8F] mt-auto" />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {currentLang === 'en' ? 'Quality Assurance' : 'ಗುಣಮಟ್ಟ ಖಾತ್ರಿ'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentLang === 'en' ? 'All our products are manufactured under strict quality control measures' : 'ನಮ್ಮ ಎಲ್ಲಾ ಉತ್ಪನ್ನಗಳನ್ನು ಕಟ್ಟುನಿಟ್ಟಾದ ಗುಣಮಟ್ಟ ನಿಯಂತ್ರಣ ಕ್ರಮಗಳ ಅಡಿಯಲ್ಲಿ ತಯಾರಿಸಲಾಗುತ್ತದೆ'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-[#4A5F8F] border-opacity-20">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-[#4A5F8F] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentLang === 'en' ? 'ISO 9001:2015 Certified' : 'ISO 9001:2015 ಪ್ರಮಾಣೀಕೃತ'}
                </h3>
                <p className="text-gray-600">
                  {currentLang === 'en' ? 'Quality management system certified' : 'ಗುಣಮಟ್ಟ ನಿರ್ವಹಣೆ ವ್ಯವಸ್ಥೆ ಪ್ರಮಾಣೀಕೃತ'}
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#4A5F8F] border-opacity-20">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-[#4A5F8F] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentLang === 'en' ? 'Premium Raw Materials' : 'ಪ್ರೀಮಿಯಂ ಕಚ್ಚಾ ವಸ್ತುಗಳು'}
                </h3>
                <p className="text-gray-600">
                  {currentLang === 'en' ? 'Only finest quality materials used' : 'ಅತ್ಯುತ್ತಮ ಗುಣಮಟ್ಟದ ವಸ್ತುಗಳನ್ನು ಮಾತ್ರ ಬಳಸಲಾಗುತ್ತದೆ'}
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#4A5F8F] border-opacity-20">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-[#4A5F8F] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentLang === 'en' ? 'Rigorous Testing' : 'ಕಠಿಣ ಪರೀಕ್ಷೆ'}
                </h3>
                <p className="text-gray-600">
                  {currentLang === 'en' ? 'Every batch thoroughly tested' : 'ಪ್ರತಿ ಬ್ಯಾಚ್ ಅನ್ನು ಸಂಪೂರ್ಣವಾಗಿ ಪರೀಕ್ಷಿಸಲಾಗುತ್ತದೆ'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/9nq0uhle_Gemini_Generated_Image_x576pbx576pbx576.png" 
              alt="Dharani Pipes installation at major infrastructure project" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#4A5F8F] to-transparent opacity-80 flex items-center">
              <div className="text-white px-12">
                <h3 className="text-3xl font-bold mb-4">
                  {currentLang === 'en' ? 'Trusted by Professionals' : 'ವೃತ್ತಿಪರರಿಂದ ವಿಶ್ವಾಸಾರ್ಹ'}
                </h3>
                <p className="text-xl">
                  {currentLang === 'en' ? 'Quality products for agriculture, construction, and infrastructure projects' : 'ಕೃಷಿ, ನಿರ್ಮಾಣ ಮತ್ತು ಮೂಲಸೌಕರ್ಯ ಯೋಜನೆಗಳಿಗಾಗಿ ಗುಣಮಟ್ಟದ ಉತ್ಪನ್ನಗಳು'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
